import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip
} from 'recharts';
import {
  DASHBOARD_DATA_STATE,
  DASHBOARD_WIDGET_TYPE
} from './dashboardContract';
import {
  buildDashboardDistributionData,
  dashboardDistributionHasMissingData,
  formatDashboardProgressPercentage,
  formatDashboardWidgetMetricValue,
  formatDashboardWidgetValue,
  getDashboardProgressColour,
  sortCalculatedWidgetMetrics
} from './dashboardWidgetViewModel';

const DEFAULT_WIDGET_COLOUR = '#6D7785';

const EffectiveDate = ({ effectiveDate }) => (
  <Typography color="text.secondary" fontSize={12}>
    {effectiveDate
      ? `Effective ${dayjs(effectiveDate).format('MMM D, YYYY')}`
      : 'No approved value yet'}
  </Typography>
);

const NumericWidget = ({ widget }) => {
  const metric = sortCalculatedWidgetMetrics(widget.metrics)[0];
  const hasData = metric?.dataState === DASHBOARD_DATA_STATE.AVAILABLE;

  return (
    <Stack sx={{ flex: 1 }}>
      {metric && (
        <Typography color="text.secondary" fontSize={13} fontWeight={600}>
          {metric.label}
        </Typography>
      )}
      <Typography
        component="p"
        sx={{
          color: hasData ? 'text.primary' : 'text.secondary',
          fontSize: { xs: 30, lg: 36 },
          fontWeight: 700,
          lineHeight: 1.2,
          mt: 1,
          overflowWrap: 'anywhere'
        }}
      >
        {formatDashboardWidgetMetricValue(metric)}
      </Typography>
      <Box sx={{ mt: 'auto', pt: 2 }}>
        <EffectiveDate effectiveDate={hasData ? metric?.effectiveDate : null} />
      </Box>
    </Stack>
  );
};

const ProgressWidget = ({ widget }) => {
  const metric = sortCalculatedWidgetMetrics(widget.metrics)[0];
  const calculation = widget.calculation ?? {};
  const percentage = Number.isFinite(Number(calculation.percentage))
    ? Math.min(100, Math.max(0, Number(calculation.percentage)))
    : 0;
  const hasData =
    calculation.percentage !== null && calculation.percentage !== undefined;
  const statusColour = getDashboardProgressColour(calculation.progressStatus);

  return (
    <Stack alignItems="center" spacing={2} sx={{ flex: 1, pt: 0.5 }}>
      <Box
        aria-label={`${widget.name} progress: ${formatDashboardProgressPercentage(
          calculation.percentage
        )}`}
        role="img"
        sx={{ display: 'inline-flex', position: 'relative' }}
      >
        <CircularProgress
          aria-hidden="true"
          size={148}
          sx={{ color: 'action.hover' }}
          thickness={4.5}
          value={100}
          variant="determinate"
        />
        <CircularProgress
          aria-hidden="true"
          size={148}
          sx={{ color: statusColour, left: 0, position: 'absolute' }}
          thickness={4.5}
          value={percentage}
          variant="determinate"
        />
        <Box
          sx={{
            alignItems: 'center',
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            left: 0,
            position: 'absolute',
            right: 0,
            top: 0
          }}
        >
          <Typography
            color={hasData ? 'text.primary' : 'text.secondary'}
            fontSize={hasData ? 28 : 16}
            fontWeight={700}
          >
            {formatDashboardProgressPercentage(calculation.percentage)}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ textAlign: 'center' }}>
        <Typography fontWeight={700}>
          {formatDashboardWidgetMetricValue(metric)}
        </Typography>
        <Typography color="text.secondary" fontSize={12}>
          Target {formatDashboardWidgetValue(metric, calculation.targetValue)}
        </Typography>
        <Typography
          color={statusColour}
          fontSize={12}
          fontWeight={700}
          sx={{ mt: 0.5, textTransform: 'capitalize' }}
        >
          {calculation.progressStatus
            ? `${calculation.progressStatus} status`
            : 'Waiting for approved data'}
        </Typography>
      </Box>
      <Box sx={{ mt: 'auto !important' }}>
        <EffectiveDate effectiveDate={calculation.effectiveDate} />
      </Box>
    </Stack>
  );
};

const DistributionWidget = ({ widget }) => {
  const data = buildDashboardDistributionData(widget);
  const metric = sortCalculatedWidgetMetrics(widget.metrics)[0];
  const doughnut = widget.widgetType === DASHBOARD_WIDGET_TYPE.DOUGHNUT;
  const totalValue = widget.calculation?.totalValue;

  if (!data.length) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          color: 'text.secondary',
          display: 'flex',
          flex: 1,
          justifyContent: 'center',
          minHeight: 220,
          textAlign: 'center'
        }}
      >
        <Typography>No positive approved values to chart.</Typography>
      </Box>
    );
  }

  return (
    <Stack sx={{ flex: 1 }}>
      <Box
        aria-label={`${widget.name}: ${data
          .map((item) => `${item.label} ${item.formattedValue}`)
          .join(', ')}`}
        role="img"
        sx={{ height: 260, position: 'relative', width: '100%' }}
      >
        <ResponsiveContainer height="100%" width="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              innerRadius={doughnut ? '55%' : 0}
              nameKey="label"
              outerRadius="82%"
              paddingAngle={data.length > 1 ? 2 : 0}
              stroke="none"
            >
              {data.map((item) => (
                <Cell fill={item.colour} key={item.metricDefinitionId} />
              ))}
            </Pie>
            <ChartTooltip
              formatter={(_value, _name, details) => [
                details?.payload?.formattedValue,
                details?.payload?.label
              ]}
            />
            {widget.showLegend && <Legend verticalAlign="bottom" />}
          </PieChart>
        </ResponsiveContainer>
        {doughnut && (
          <Box
            sx={{
              alignItems: 'center',
              bottom: widget.showLegend ? 36 : 0,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              left: 0,
              pointerEvents: 'none',
              position: 'absolute',
              right: 0,
              top: 0
            }}
          >
            <Typography color="text.secondary" fontSize={11}>
              Total
            </Typography>
            <Typography fontSize={18} fontWeight={700}>
              {formatDashboardWidgetValue(metric, totalValue)}
            </Typography>
          </Box>
        )}
      </Box>
      {dashboardDistributionHasMissingData(widget) && (
        <Typography
          color="warning.main"
          fontSize={12}
          fontWeight={600}
          sx={{ mt: 1, textAlign: 'center' }}
        >
          Some metrics do not yet have an approved value.
        </Typography>
      )}
    </Stack>
  );
};

const DashboardWidgetCard = ({ widget }) => {
  const firstMetric = sortCalculatedWidgetMetrics(widget.metrics)[0];
  const accentColour = firstMetric?.colour || DEFAULT_WIDGET_COLOUR;
  const distribution =
    widget.widgetType === DASHBOARD_WIDGET_TYPE.PIE ||
    widget.widgetType === DASHBOARD_WIDGET_TYPE.DOUGHNUT;

  return (
    <Paper
      data-testid={`dashboard-widget-${widget.dashboardWidgetId}`}
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderTop: '5px solid',
        borderTopColor: distribution ? 'primary.main' : accentColour,
        display: 'flex',
        flexDirection: 'column',
        minHeight: distribution ? 390 : 330,
        minWidth: 0,
        p: { xs: 2, md: 2.5 }
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography component="h3" fontSize={17} fontWeight={700}>
          {widget.name}
        </Typography>
        {widget.description && (
          <Typography color="text.secondary" fontSize={12} sx={{ mt: 0.5 }}>
            {widget.description}
          </Typography>
        )}
      </Box>

      {widget.widgetType === DASHBOARD_WIDGET_TYPE.NUMERIC && (
        <NumericWidget widget={widget} />
      )}
      {widget.widgetType === DASHBOARD_WIDGET_TYPE.PROGRESS && (
        <ProgressWidget widget={widget} />
      )}
      {distribution && <DistributionWidget widget={widget} />}
    </Paper>
  );
};

export default DashboardWidgetCard;
